import { useState } from "react";

import {
  getPatientDocuments,
  uploadPatientDocument,
} from "app/services/transmission/patient";

import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_DOWNLOADING_TEXT, FILE_CONTENT_TYPE } from "static/constants";
import { showSuccessToast, showToast } from "utils/toast";

import {
  generateLetterTemplate,
  saveLetterTemplate,
  sendNoShowEmail,
  getLetterNextSend,
} from "app/services/transmission/letter_templates";

import {
  setDownloadingModalText,
  setGeneratedLetterFilePath,
  setIsDocumentTableLoading,
  setIsDocumentsLoading,
  setIsDownloading,
  setIsLetterTemplateMenu,
  setIsUploadModalOpen,
  setSelectedDocument,
  setShowBatteryInformationModal,
  setLetterNextSendDateModal,
  setLetterNextSendDate,
  setPendingBatteryLife,
  setShowDownloadTemplate,
  setShowNoShowEmailModal,
  setShowPreviewMode,
} from "app/store/patientProfile/documents/slice/patientProfileDocumentSlice";
import { changeProfileField } from "app/store/transmission/slice/transmissionSlice";
import { convertBytesToMB } from "utils/common";
import useDocumentsTableColumn from "./columns/useDocumentsTableColumn";

const useDocuments = () => {
  const { profile, information } = useSelector((state) => state.transmission);
  const {
    selectedTemplate,
    selectedDocument,
    generatedLetterFilePath,
    pendingBatteryLife,
  } = useSelector((state) => state.patientProfileDocument);

  const [tableData, setTableData] = useState([]);

  const dispatch = useDispatch();
  const { handleFilePreview, handleFileDownload } = useDocumentsTableColumn();

  const fetchTableData = async () => {
    dispatch(setIsDocumentTableLoading(true));
    try {
      const result = await getPatientDocuments(profile?.serial_number);
      setTableData(result?.data || []);
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      dispatch(setIsDocumentTableLoading(false));
    }
  };

  const handleFileUpload = async (document) => {
    try {
      dispatch(setIsDocumentsLoading(true));
      const payload = {
        serialNumber: profile?.serial_number,
        document,
      };

      const response = await uploadPatientDocument(payload);

      if (!response.error) {
        dispatch(setIsUploadModalOpen(false));
        showToast({ message: response.message });
        await fetchTableData();
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      dispatch(setIsDocumentsLoading(false));
    }
  };

  const handleSaveLetterTemplate = async () => {
    try {
      dispatch(setDownloadingModalText("Saving generated letter..."));
      dispatch(setIsDownloading(true));

      const { filePath, fileSize, fileName } = selectedDocument;

      const payload = {
        patientId: profile.patientId,
        fileProps: {
          key: filePath,
          fileSizeInMB: fileSize,
          originalname: fileName,
          mimetype: FILE_CONTENT_TYPE.PDF,
        },
      };

      const response = await saveLetterTemplate(payload);

      if (!response.error) {
        showToast({ message: response.message });
        dispatch(setShowPreviewMode(true));
        await fetchTableData();
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      dispatch(setIsDownloading(false));
      dispatch(setDownloadingModalText(DEFAULT_DOWNLOADING_TEXT));
    }
  };

  const processNoShowEmail = async (email, date) => {
    try {
      const { templateId } = selectedDocument;
      const { patientId, firstName, account } = profile;
      const payload = {
        templateId,
        patientInformation: {
          patientId,
          firstName,
          account,
          email,
        },
      };

      if (date) {
        payload["date"] = date;
      }

      dispatch(setIsDownloading(true));
      dispatch(setDownloadingModalText("Sending email"));

      const response = await sendNoShowEmail(payload);

      if (response) {
        showSuccessToast(response.message);
        dispatch(setShowNoShowEmailModal(false));
        await fetchTableData();
        dispatch(
          changeProfileField({
            type: "information",
            value: { ...information, email },
          })
        );
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      dispatch(setIsDownloading(false));
      dispatch(setDownloadingModalText(DEFAULT_DOWNLOADING_TEXT));
    }
  };

  const handleDownloadLetter = async () => {
    try {
      await handleFileDownload({ filePath: generatedLetterFilePath });
      dispatch(setShowDownloadTemplate(false));
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    }
  };

  const handleBatteryInformation = async (batteryLife) => {
    dispatch(setShowBatteryInformationModal(false));

    if (selectedTemplate?.custom_next_send) {
      try {
        dispatch(setDownloadingModalText("Calculating next send date..."));
        dispatch(setIsDownloading(true));

        const nextSendInfo = await getLetterNextSend({
          templateId: selectedTemplate._id,
          patientId: profile.patientId,
        });

        dispatch(setPendingBatteryLife(batteryLife));
        dispatch(setLetterNextSendDate(nextSendInfo?.data?.nextSendDate));
        dispatch(setLetterNextSendDateModal(true));
      } catch (error) {
        showToast({ message: error.message, type: "error" });
      } finally {
        dispatch(setIsDownloading(false));
      }
      return;
    }

    generateTemplate({ template: selectedTemplate, batteryLife });
  };

  const handleLetterNextSendDate = (letterNextSendDate) => {
    dispatch(setLetterNextSendDateModal(false));

    generateTemplate({
      template: selectedTemplate,
      batteryLife: pendingBatteryLife,
      letterNextSendDate,
    });

    dispatch(setPendingBatteryLife(null));
  };

  const generateTemplate = async ({
    template,
    batteryLife,
    letterNextSendDate,
  }) => {
    const { _id } = template;
    dispatch(setDownloadingModalText("Generating..."));
    await handleGenerateLetter(_id, batteryLife, letterNextSendDate);
  };

  const handleGenerateLetter = async (
    templateId,
    batteryLife,
    letterNextSendDate
  ) => {
    try {
      await dispatch(setIsDownloading(true));
      dispatch(setShowPreviewMode(false));

      const payload = {
        templateId,
        patientId: profile.patientId,
      };

      if (selectedTemplate?.show_battery && batteryLife) {
        payload.batteryLife = batteryLife;
      }

      if (letterNextSendDate) {
        payload.customNextSendDate = letterNextSendDate;
      }

      const {
        data: { letterProps },
        message,
      } = await generateLetterTemplate(payload);

      const { size, key: filePath, originalname: fileName } = letterProps;
      const rowData = {
        filePath,
        fileName,
        fileSize: `${convertBytesToMB(size)} MB`,
      };

      showToast({ message });
      dispatch(setGeneratedLetterFilePath(filePath));
      dispatch(setSelectedDocument(rowData));
      await handleFilePreview(rowData, filePath);
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      dispatch(setIsDownloading(false));
      dispatch(setIsLetterTemplateMenu(false));
      dispatch(setDownloadingModalText(DEFAULT_DOWNLOADING_TEXT));
    }
  };

  return {
    profile,
    tableData,
    information,
    dispatch,
    fetchTableData,
    handleFileUpload,
    generateTemplate,
    handleDownloadLetter,
    processNoShowEmail,
    handleSaveLetterTemplate,
    handleBatteryInformation,
    handleLetterNextSendDate,
  };
};

export default useDocuments;
